import React, {useState} from 'react';
import {
   ControlButton,
   CustomSelectInput,
   SelectInput,
   SlideToggle,
   Tabs,
   TscButtonType,
   TscStyles,
   TscThemeName,
   TscThemeNames
} from '@techsmith/tsc-cloud-style-guide';
import {MediaPrivacy} from '../../model/mediaDetails';
import {Trans, useTranslation} from 'react-i18next';
import {MediaPrivacyOptionModel} from '../../model/mediaPrivacyOptionModel';
import {getMediaPrivacyOptions} from '../../util/MediaPrivacyOptionsGenerator';
import '../../static/css/modal.less';
import styled from 'styled-components';
import {ITab} from '../../model/tab';
import ShareMediaLink from './ShareMediaLink';
import {MinWidthButtonWrapper, StyledWarning} from '../util/StyledElements';
import EmbedCodeFormField from './EmbedCodeFormField';
import {IWorkerState, SetupWorkerState} from '../../model/workerState';
import mediaApi from '../../service/mediaApi';
import ConfirmPrivacyChangeModal from './ConfirmPrivacyChangeModal';
import {CaptioningTypes, ICaptionData} from '../../model/captionModels';
import config from '../../service/config';
import CssUtil from '../../util/CssUtil';
import {IConversationData} from '../../model/conversationModel';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {conversationsStore} from '../../context/conversationsProvider';
import Constants, {LibraryTypes} from '../../constants/Constants';
import {selectedConversationStore} from '../../context/selectedConversationProvider';
import {useTscTheme} from '../../context/themeProvider';
import {mediaStore, updateMediaState} from '../../context/mediaProvider';
import {captionDataStore} from '../../context/captionDataProvider';
import {BasicModalPortal} from '../util/ModalPortalHelper';
import SetAccessPhrase from './SetAccessPhrase';
import {IBaseLibrary} from '../../model/library';
import accessPhraseApi from '../../service/accessPhraseApi';

const PrivacySelectWrapper = styled.div`
   padding-right: 2px;
   text-align: start;
`;

const ShareOptionsContainer = styled.div`
   margin-bottom: 1rem;
`;

const AccessPhraseToggle = styled.div`
   margin-bottom: 1rem;
`;

const backgroundAlpha = 0.1;

const AccessPhraseMessage = styled.span`
   font-size: 0.8125rem;
`;

const ShareOptionsWrapper = styled.div<{theme: TscThemeNames; hasWarning: boolean}>`
   display: flex;
   flex-direction: column;
   background-color: ${props =>
      props.hasWarning ? CssUtil.toRgba(TscStyles.color.message.caution, backgroundAlpha) :
         props.theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.mediumDark :
            TscStyles.color.ui.dawn.lightMedium};
   border: 1px solid ${props =>
      props.hasWarning ? TscStyles.color.message.caution :
         props.theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.dark :
            TscStyles.color.ui.dawn.medium};
   border-radius: ${TscStyles.border.radius.lg};
`;

const AccessPhraseWrapper = styled.div<{theme: TscThemeNames}>`
   display: flex;
   flex-direction: column;
   background-color: ${props =>
      props.theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.mediumDark :
         TscStyles.color.ui.dawn.lightMedium};
   border: 1px solid ${props =>
      props.theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.dark :
         TscStyles.color.ui.dawn.medium};
   border-radius: ${TscStyles.border.radius.lg};
`;

const TabsHeaderWrapper = styled.div<{theme: TscThemeNames}>`
   height: 2.25rem;
   border-bottom: 1px solid ${props => props.theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.dark : TscStyles.color.ui.dawn.medium};

   > div {
      height: 2.25rem;

      button {
         margin: 0;
      }

      span {
         font-size: 0.8125rem;
         text-transform: uppercase;
         font-weight: 600;
         color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.text.light : TscStyles.color.text.mediumDark};
         height: 100%;
         display: flex;
         justify-content: center;
         align-items: center;
      }
   }
`;

const TabBodyWrapper = styled.div`
   padding: 1rem 1rem 0 1rem;
`;

const InvalidMediaPrivacyWrapper = styled.span`
   text-align: center;
   padding: 1rem;
`;

const BoldText = styled.span`
   font-weight: ${TscStyles.font.weight.semibold};
   padding-left: 0.25rem;
   display: inline-flex;
`;

export const ShareModalBase: React.FC<IShareModalProps & IStateMappedProps> = ({mediaLibraryType, onClose, HasAccessPhrase, conversations, VideoReviewId, Privacy, Hash, OwnerLibrary, CaptioningAssignedToEmail, Url, MediaStoreWorkerState, captionData}) => {
   const {t} = useTranslation();
   const theme = useTscTheme();
   const [options] = useState<MediaPrivacyOptionModel[]>([...getMediaPrivacyOptions(mediaLibraryType).values()]);
   const [isGetLinkSelected, setIsGetLinkSelected] = useState<boolean>(true);
   const [state, setState] = SetupWorkerState();
   const [showPrivateConfirmationModal, setShowPrivateConfirmationModal] = useState(false);
   const [warningMessages, setWarningMessages] = useState<string[]>([]);
   const [selectPrivacyOption, setSelectPrivacyOption] = useState<MediaPrivacy>(Privacy);
   const [selectedConversationId, setSelectedConversationId] = useState<string>(VideoReviewId);
   const noConversationKey = 'shareWithoutConversation';
   const conversationOptions = [{value: noConversationKey, label: t('share.shareWithoutConversation')}];
   const [currentCopy, setCurrentCopy] = useState<string>('');
   const [isAccessPhraseEnabled, setIsAccessPhraseEnabled] = useState(HasAccessPhrase && mediaLibraryType !== LibraryTypes.team);

   conversations?.forEach(conversation => {
      conversationOptions.push({value: conversation.VideoReviewId, label: conversation.Title});
   });

   React.useEffect(() => {
      const messages = [];
      if (CaptioningAssignedToEmail) {
         messages.push(t('confirmPrivacyChange.assignee'));
      }
      if (captionData?.filter(c => c.CaptioningType === CaptioningTypes.ThirdParty).length > 0) {
         messages.push(t('confirmPrivacyChange.captions'));
      }

      setWarningMessages(messages);
   }, [Hash, captionData]);

   React.useEffect(() => {
      setSelectPrivacyOption(Privacy);
   }, [Privacy]);

   React.useEffect(() => setState({...state, isWorking: MediaStoreWorkerState.isWorking, hasError: MediaStoreWorkerState.hasError}), [MediaStoreWorkerState]);

   const updatePrivacy = async (newPrivacy: MediaPrivacy) => {
      setState({...state, hasError: false});
      if (Privacy !== newPrivacy) {
         setState({...state, isWorking: true});
         try {
            await mediaApi.updatePrivacy(Hash, newPrivacy);
            updateMediaState({Privacy: newPrivacy});
            setState({isWorking: false, hasError: false});
         } catch {
            setState({isWorking: false, hasError: true});
         }
      }
   };

   const handleOptionSelect = async (newPrivacy: MediaPrivacy) => {
      setSelectPrivacyOption(newPrivacy);
      if (newPrivacy === MediaPrivacy.private && warningMessages.length > 0) {
         setShowPrivateConfirmationModal(true);
      } else {
         await updatePrivacy(newPrivacy);
      }
   };

   const onConfirmClick = async () => {
      setShowPrivateConfirmationModal(false);
      await updatePrivacy(MediaPrivacy.private);
   };

   const onCloseClick = () => {
      setSelectPrivacyOption(Privacy);
      setShowPrivateConfirmationModal(false);
   };

   const updateSelectedConversationId = (e: React.ChangeEvent<HTMLSelectElement>) => {
      if (e.target.value === noConversationKey) {
         setSelectedConversationId('');
      } else {
         setSelectedConversationId(e.target.value);
      }
   };

   const onToggleAccessPhraseClick = async () => {
      if (isAccessPhraseEnabled) {
         await mediaApi.clearMediaAccessPhrase(Hash);
      } else {
         await mediaApi.updateMediaAccessPhrase(Hash, await accessPhraseApi.generateAccessPhrase());
      }
      setIsAccessPhraseEnabled(!isAccessPhraseEnabled);
      updateMediaState({HasAccessPhrase: !isAccessPhraseEnabled});
   };

   const tabs: ITab[] = [
      {
         text: t('share.linkTabText'),
         isSelected: isGetLinkSelected,
         onClick: () => setIsGetLinkSelected(true)
      }, {
         text: t('share.embedTabText'),
         isSelected: !isGetLinkSelected,
         onClick: () => setIsGetLinkSelected(false)
      }
   ];

   const privacySetting = getMediaPrivacyOptions(mediaLibraryType).get(MediaPrivacy.private).label;

   return (
      <>
         <BasicModalPortal
            testId="share-modal"
            visible={true}
            title={t('share.shareMedia')}
            width="30rem"
            onClose={onClose}
            themeName={theme}
            enableFocusTrap={true}
            isLoading={state.isWorking}
         >
            <div className="modal-body">
               <div className="form-body themeable-section">
                  {config.showPrivacySettingOnShareModal && (
                     <div className="form-field-listing">
                        <PrivacySelectWrapper className="form-field-group">
                           {state.hasError && (
                              <StyledWarning>
                                 {t('general.errorText')}
                              </StyledWarning>
                           )}
                           <CustomSelectInput
                              testId="privacy-custom-select-input"
                              themeName={theme}
                              options={options}
                              width="100%"
                              closeMenuOnClick={true}
                              label={t('privacy.media.tabHeader')}
                              onChange={handleOptionSelect}
                              value={selectPrivacyOption}
                           />
                        </PrivacySelectWrapper>
                     </div>
                  )}
                  <ShareOptionsContainer className="form-field-listing">
                     <ShareOptionsWrapper
                        theme={theme}
                        hasWarning={Privacy === MediaPrivacy.private && mediaLibraryType !== LibraryTypes.team}
                     >
                        {(Privacy === MediaPrivacy.private && mediaLibraryType !== LibraryTypes.team) ? (
                           <InvalidMediaPrivacyWrapper>
                              <Trans i18nKey="share.invalidPrivacyState">
                                 Media set to
                                 <BoldText>
                                    {privacySetting}
                                 </BoldText>
                                 .
                                 cannot be shared. Change the privacy setting to get a shareable link.
                              </Trans>
                           </InvalidMediaPrivacyWrapper>
                        ) : (
                           <>
                              <TabsHeaderWrapper theme={theme}>
                                 <Tabs
                                    tabs={tabs}
                                    themeName={theme}
                                 />
                              </TabsHeaderWrapper>
                              <TabBodyWrapper>
                                 {isGetLinkSelected && (
                                    <>
                                       {!config.featureSwitches.UseSingleConversation && !!conversations?.length && (
                                          <div className="form-field-listing form-field-group">
                                             <SelectInput
                                                testId="conversation-select-input"
                                                themeName={theme}
                                                value={selectedConversationId}
                                                onChange={updateSelectedConversationId}
                                                options={conversationOptions}
                                                label={t('share.conversation')}
                                             />
                                          </div>
                                       )}
                                       <ShareMediaLink
                                          link={!config.featureSwitches.UseSingleConversation && selectedConversationId ? `${Url}?${Constants.queryParams.conversation}=${selectedConversationId}` : Url}
                                          text={t('share.linkText')}
                                          setCurrentCopy={(e: string) => setCurrentCopy(e)}
                                          currentCopy={currentCopy}
                                       />
                                    </>
                                 )}
                                 {!isGetLinkSelected && <EmbedCodeFormField />}
                              </TabBodyWrapper>
                           </>
                        )}
                     </ShareOptionsWrapper>
                  </ShareOptionsContainer>
                  {(config.featureSwitches.EnableMediaAccessPhrase && (config.isAdmin || config.user.TechSmithId === OwnerLibrary.User.TechSmithId || config.teamConfig?.loggedInUserHasCreatePermission) && Privacy !== MediaPrivacy.private) && (
                     <ShareOptionsContainer>
                        <AccessPhraseWrapper theme={theme}>
                           <TabBodyWrapper>
                              <AccessPhraseToggle>
                                 <SlideToggle
                                    labelText={t('accessPhrase.protectedMessage')}
                                    isChecked={isAccessPhraseEnabled}
                                    onClick={onToggleAccessPhraseClick}
                                 />
                              </AccessPhraseToggle>
                              {isAccessPhraseEnabled && (
                                 <>
                                    <AccessPhraseMessage>
                                       {t('accessPhrase.message')}
                                    </AccessPhraseMessage>
                                    <SetAccessPhrase
                                       mediaHash={Hash}
                                       setCurrentCopy={(e: string) => setCurrentCopy(e)}
                                       currentCopy={currentCopy}
                                    />
                                 </>
                              )}
                           </TabBodyWrapper>
                        </AccessPhraseWrapper>
                     </ShareOptionsContainer>
                  )}
                  <div className="button-group">
                     <MinWidthButtonWrapper>
                        <ControlButton
                           label={t('general.close')}
                           buttonType={TscButtonType.secondary}
                           themeName={theme}
                           onClick={onClose}
                           testId="share-modal-close-button"
                        />
                     </MinWidthButtonWrapper>
                  </div>
               </div>
            </div>
         </BasicModalPortal>
         {showPrivateConfirmationModal && (
            <ConfirmPrivacyChangeModal
               onClose={onCloseClick}
               onConfirm={onConfirmClick}
               warnings={warningMessages}
            />
         )}
      </>
   );
};

export interface IShareModalProps {
   mediaLibraryType: LibraryTypes;
   onClose(): void;
}

export interface IStateMappedProps {
   conversations: IConversationData[];
   VideoReviewId: string;
   Privacy: MediaPrivacy;
   Hash: string;
   OwnerLibrary: IBaseLibrary;
   CaptioningAssignedToEmail: string;
   Url: string;
   captionData: ICaptionData[];
   HasAccessPhrase: boolean;
   MediaStoreWorkerState: IWorkerState;
}

const mapStatesToProps = (
   {mediaLibraryType, onClose}: IShareModalProps,
   {conversations}: Partial<IStateMappedProps>,
   {VideoReviewId}: Partial<IStateMappedProps>,
   {Privacy, Hash, OwnerLibrary, CaptioningAssignedToEmail, Url, HasAccessPhrase, MediaStoreWorkerState}: Partial<IStateMappedProps>,
   {captionData}: Partial<IStateMappedProps>
): IShareModalProps & IStateMappedProps => ({mediaLibraryType, onClose, conversations, VideoReviewId, Privacy, Hash, OwnerLibrary, CaptioningAssignedToEmail, Url, HasAccessPhrase, MediaStoreWorkerState, captionData});

export default withMemoizedContexts(mapStatesToProps, conversationsStore, selectedConversationStore, mediaStore, captionDataStore)(ShareModalBase);
